import { type Static, Type } from "@sinclair/typebox"
import {
  IdSchema,
  IsoTimestampSchema,
  NonEmptyStringSchema,
  SeatIdSchema,
} from "./common"
import { CardInstanceStateSchema } from "./instances"
import { SystemBattleBotsConfigSchema } from "./system"

export type { SeatId, ZoneId } from "./common"
export { SeatIdSchema, ZoneIdSchema } from "./common"

export const CentralCommandStateSchema = Type.Object({
  name: Type.Literal("Central Command"),
  baseHealth: Type.Integer({
    minimum: 1,
  }),
  handicapHealthBonus: Type.Integer({
    minimum: 0,
  }),
  currentHealth: Type.Integer({
    minimum: 0,
  }),
})

export type CentralCommandState = Static<typeof CentralCommandStateSchema>

export const ResourceStateSchema = Type.Object({
  maxEnergy: Type.Integer({
    minimum: 0,
  }),
  currentEnergy: Type.Integer({
    minimum: 0,
  }),
})

export type ResourceState = Static<typeof ResourceStateSchema>

export const HeroRuntimeStateSchema = Type.Object({
  heroId: IdSchema,
  currentLevel: Type.Union([Type.Literal(1), Type.Literal(2), Type.Literal(3)]),
  levelLockedUntilTurn: Type.Optional(
    Type.Integer({
      minimum: 1,
    }),
  ),
  currentInstanceId: IdSchema,
})

export type HeroRuntimeState = Static<typeof HeroRuntimeStateSchema>

export const SeatZonesSchema = Type.Object({
  active: Type.Union([IdSchema, Type.Null()]),
  support: Type.Array(IdSchema),
  hand: Type.Array(IdSchema),
  drawDeck: Type.Array(IdSchema),
  discard: Type.Array(IdSchema),
  scrap: Type.Array(IdSchema),
  supply: Type.Array(IdSchema),
  mercRow: Type.Array(IdSchema, {
    maxItems: 3,
  }),
})

export type SeatZones = Static<typeof SeatZonesSchema>

export const SeatTurnFlagsSchema = Type.Object({
  hasUsedNormalSwap: Type.Boolean(),
  hasRecruitedThisTurn: Type.Boolean(),
})

export type SeatTurnFlags = Static<typeof SeatTurnFlagsSchema>

export const SeatStateSchema = Type.Object({
  seatId: SeatIdSchema,
  playerId: IdSchema,
  displayName: NonEmptyStringSchema,
  centralCommand: CentralCommandStateSchema,
  resources: ResourceStateSchema,
  hero: HeroRuntimeStateSchema,
  zones: SeatZonesSchema,
  deckRef: IdSchema,
  oncePerTurn: SeatTurnFlagsSchema,
})

export type SeatState = Static<typeof SeatStateSchema>

export const HandicapConfigSchema = Type.Object({
  seatABonusHealth: Type.Integer({
    minimum: 0,
  }),
  seatBBonusHealth: Type.Integer({
    minimum: 0,
  }),
})

export type HandicapConfig = Static<typeof HandicapConfigSchema>

export const MatchConfigSchema = Type.Object({
  mode: Type.Literal("friend_pvp"),
  randomSeed: NonEmptyStringSchema,
  startingCentralCommandHealth: Type.Integer({
    minimum: 1,
  }),
  supportCapacity: Type.Integer({
    minimum: 1,
  }),
  handSizeLimit: Type.Integer({
    minimum: 1,
  }),
  startingHandSize: Type.Integer({
    minimum: 1,
  }),
  energyCurveMax: Type.Integer({
    minimum: 1,
  }),
  systemBattleBots: SystemBattleBotsConfigSchema,
  handicap: Type.Optional(HandicapConfigSchema),
})

export type MatchConfig = Static<typeof MatchConfigSchema>

export const MatchLogEntrySchema = Type.Object({
  sequence: Type.Integer({
    minimum: 0,
  }),
  turn: Type.Integer({
    minimum: 1,
  }),
  seatId: SeatIdSchema,
  eventType: NonEmptyStringSchema,
  payload: Type.Record(Type.String(), Type.Unknown()),
  timestamp: Type.Optional(IsoTimestampSchema),
})

export type MatchLogEntry = Static<typeof MatchLogEntrySchema>

export const MatchResultSchema = Type.Object({
  winnerSeat: SeatIdSchema,
  loserSeat: SeatIdSchema,
  reason: Type.Union([
    Type.Literal("central_command_zero"),
    Type.Literal("concede"),
    Type.Literal("timeout"),
  ]),
  finalTurn: Type.Integer({
    minimum: 1,
  }),
})

export type MatchResult = Static<typeof MatchResultSchema>

export const SeatConnectionStatusSchema = Type.Union([
  Type.Literal("connected"),
  Type.Literal("disconnected"),
])

export type SeatConnectionStatus = Static<typeof SeatConnectionStatusSchema>

export const MatchSeatConnectionStateSchema = Type.Object({
  seat_a: SeatConnectionStatusSchema,
  seat_b: SeatConnectionStatusSchema,
})

export type MatchSeatConnectionState = Static<
  typeof MatchSeatConnectionStateSchema
>

export const MatchSeatDisconnectDeadlinesSchema = Type.Object({
  seat_a: Type.Optional(
    Type.Integer({
      minimum: 0,
    }),
  ),
  seat_b: Type.Optional(
    Type.Integer({
      minimum: 0,
    }),
  ),
})

export type MatchSeatDisconnectDeadlines = Static<
  typeof MatchSeatDisconnectDeadlinesSchema
>

export const MatchStateSchema = Type.Object({
  matchId: IdSchema,
  rulesEdition: Type.Integer({
    minimum: 0,
  }),
  rulesRevision: Type.Integer({
    minimum: 0,
  }),
  contentVersion: NonEmptyStringSchema,
  status: Type.Union([
    Type.Literal("setup"),
    Type.Literal("active"),
    Type.Literal("completed"),
  ]),
  config: MatchConfigSchema,
  seats: Type.Tuple([SeatStateSchema, SeatStateSchema]),
  activeSeat: SeatIdSchema,
  prioritySeat: SeatIdSchema,
  turn: Type.Object({
    number: Type.Integer({
      minimum: 1,
    }),
    step: Type.Union([
      Type.Literal("start"),
      Type.Literal("recover"),
      Type.Literal("energy"),
      Type.Literal("draw"),
      Type.Literal("main"),
      Type.Literal("cleanup"),
    ]),
    actionWindow: Type.Union([Type.Literal("open"), Type.Literal("closed")]),
    actionCount: Type.Integer({
      minimum: 0,
    }),
  }),
  rngCallIndex: Type.Integer({
    minimum: 0,
  }),
  instances: Type.Record(Type.String(), CardInstanceStateSchema),
  log: Type.Array(MatchLogEntrySchema),
  result: Type.Optional(MatchResultSchema),
})

export type MatchState = Static<typeof MatchStateSchema>
